import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  ListTodo, 
  PlusCircle, 
  BarChart3, 
  Users, 
  LogOut,
  AlertTriangle,
  Menu,
  X,
  Wrench,
  ClipboardList
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["FO", "HOD", "DM", "GM"] },
    { path: "/issues", label: "Command Queue", icon: ListTodo, roles: ["FO", "HOD", "DM", "GM"] },
    { path: "/room-pm", label: "RPM", icon: Wrench, roles: ["HOD", "DM", "GM"] },
    { path: "/work-orders", label: "Work Orders", icon: ClipboardList, roles: ["HOD", "DM", "GM"] },
    { path: "/new-issue", label: "New Issue", icon: PlusCircle, roles: ["FO", "HOD", "DM", "GM"] },
    { path: "/analytics", label: "Analytics", icon: BarChart3, roles: ["DM", "GM"] },
    { path: "/users", label: "Users", icon: Users, roles: ["GM"] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  const roleColors = {
    GM: "bg-amber-500/20 text-amber-500 border-amber-500/30",
    DM: "bg-blue-500/20 text-blue-500 border-blue-500/30",
    HOD: "bg-emerald-500/20 text-emerald-500 border-emerald-500/30",
    FO: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-surface rounded-md border border-border"
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-surface border-r border-border transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-sm flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold tracking-tight text-primary uppercase">
                  ORBIT
                </h1>
                <p className="text-[10px] text-muted-foreground tracking-widest uppercase">
                  Risk Intelligence
                </p>
              </div>
            </div>
            {/* Property Indicator */}
            <div className="mt-3 px-2 py-1.5 bg-primary/10 rounded text-center">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Property</p>
              <p className="text-xs font-semibold text-primary">
                {user?.active_property === "GH" ? "Golden Hill" : "Golden Tulip Holland Resort"}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.slice(1)}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all ${
                    isActive
                      ? "bg-primary/10 text-primary border-l-2 border-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-surface-highlight"
                  }`}
                >
                  <item.icon size={18} />
                  <span className="font-heading uppercase tracking-wide text-xs">
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-surface-highlight rounded-full flex items-center justify-center text-sm font-bold">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${roleColors[user?.role]}`}>
                  {user?.role}
                </span>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              data-testid="logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              <span className="text-xs uppercase tracking-wide">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main id="main-scroll-container" className="flex-1 overflow-auto">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
